import LocalizedStrings from 'localized-strings';

import { TaxTypeEnum } from '__generated__/GQL';

import { getMobileAppLang } from './common/utils';
import { Quarter } from './types';

const locales = new LocalizedStrings(
  {
    en: {
      date: {
        format: 'en-US',
        quarter: {
          [Quarter.FIRST]: '1st quarter {year}',
          [Quarter.SECOND]: '2nd quarter {year}',
          [Quarter.THIRD]: '3rd quarter {year}',
          [Quarter.FOURTH]: '4th quarter {year}',
        },
      },
      errorMessage:
        'An error has occurred. Please try again and if the problem persists, please contact us.',
      faq: {
        label: 'Learn more',
        url: 'https://help.shine.fr/fr/articles/9099961-comment-fonctionne-le-nouvel-estimateur-shine-de-cotisations-sociales-pour-les-micro-entreprises',
      },
      faqURL:
        'https://help.shine.fr/fr/articles/9099961-comment-fonctionne-le-nouvel-estimateur-shine-de-cotisations-sociales-pour-les-micro-entreprises',
      feedback: {
        feedbackCTA: 'Give some feedback',
        feedbackLink:
          'https://shinetools.typeform.com/to/UPFBLMeb#email={email}&user_id={uid}',
      },
      history: {
        empty: {
          cta: 'Start my simulation',
          subtitle:
            'Once the current period is over, find your simulations right here!',
          title: 'No simulation',
        },
        markAsNotPaidSuccess:
          'Your URSSAF installment has been marked as non-paid!',
        title: 'Your URSSAF deadlines',
      },
      isPaid:
        'Contributions over this period have been paid. Only ongoing and late payment periods can be edited.',
      overview: {
        edit: 'Adjust my income',
        empty: {
          cta: 'Start my simulation',
          subtitle:
            'Anticipate your URSSAF payments, simulate your income tax and get an idea of what you really earn. Update your information at any time from the "Update my situation" button.',
          title: 'Estimate your income in real time',
        },
        netIncome: 'Remaining income',
        revenue: 'Turnover for the period',
        settings: 'Update my situation',
        socialContributions: 'Contributions',
        tax: 'Taxes',
        tooltip: {
          complementaryRevenue: 'Supplementary Income: {amount} incl. VAT',
          revenue: 'Turnover in your Shine account: {amount} incl. VAT',
          title:
            'Estimated amount based on your incoming payments (excluding payments marked as "personal" in the Shine application).',
          vatAmount: 'Collected VAT: {amount}',
        },
        vatWithAmount: 'Collected VAT: {amount}',
        withoutTax: 'excl. VAT',
      },
      readTheManual: 'Browse the instructions',
      settings: {
        cta: {
          subtitle:
            'Keep your information up to date to anticipate your URSSAF payments as accurately as possible.',
          text: 'Update my situation',
          title: 'My situation has changed',
        },
        frequency: {
          activeFrom: 'Active from {date}',
          description: 'Income tax reporting frequency',
          monthly: 'Every month',
          quarterly: 'Every quarter',
        },
        options: {
          accre: 'I benefit from the start of activity exemption or Acre',
          description: 'My options',
          libDescription: 'Settlement payment of income tax',
          liberatoire: "I've opted for settlement payment",
          vat: 'I am subject to VAT',
        },
        title: 'Set up your situation',
      },
      subtitle:
        'Estimate your micro-business contributions and consult your URSSAF payment history.',
      tabs: {
        history: 'History',
        simulator: 'Simulator',
      },
      taxDetails: {
        accordionTitle: 'Detail of contributions',
        amountPaid: 'URSSAF settlement amount',
        ctaNotPaid: 'Mark as unpaid',
        ctaPaid: 'Mark as paid',
        rate: {
          [TaxTypeEnum.Cfp]: '+ Professional training contribution',
          [TaxTypeEnum.FraisChambre]: '+ Taxes for room charges',
          [TaxTypeEnum.PrelevLib]: 'Income tax',
          [TaxTypeEnum.Rsi]: 'Social contributions',
        },
        status: {
          late: 'Late payment',
          paid: 'Paid',
          pending: 'Ongoing',
        },
        vat: 'Collected VAT',
      },
      title: 'Social contributions',
    },
    fr: {
      date: {
        format: 'fr',
        quarter: {
          [Quarter.FIRST]: '1er trimestre {year}',
          [Quarter.SECOND]: '2ème trimestre {year}',
          [Quarter.THIRD]: '3ème trimestre {year}',
          [Quarter.FOURTH]: '4ème trimestre {year}',
        },
      },
      errorMessage:
        'Une erreur est survenue. Réessayez et si le problème persiste, contactez-nous.',
      faq: {
        label: 'En savoir plus',
        url: 'https://help.shine.fr/fr/articles/9099961-comment-fonctionne-le-nouvel-estimateur-shine-de-cotisations-sociales-pour-les-micro-entreprises',
      },
      faqURL:
        'https://help.shine.fr/fr/articles/9099961-comment-fonctionne-le-nouvel-estimateur-shine-de-cotisations-sociales-pour-les-micro-entreprises',
      feedback: {
        feedbackCTA: 'Faites-nous vos retours',
        feedbackLink:
          'https://shinetools.typeform.com/to/UPFBLMeb#email={email}&user_id={uid}',
      },
      history: {
        empty: {
          cta: 'Démarrer ma simulation',
          subtitle:
            'Une fois la période terminée, retrouvez vos simulations juste ici !',
          title: 'Aucune simulation',
        },
        markAsNotPaidSuccess:
          'Votre échéance URSSAF a été marquée comme non payée avec succès !',
        title: 'Vos échéances URSSAF',
      },
      isPaid:
        'Les cotisations sur cette période ont été payées. Seules les périodes en cours et en attente de paiement peuvent être éditées.',
      overview: {
        edit: 'Ajuster mes revenus',
        empty: {
          cta: 'Démarrer ma simulation',
          subtitle:
            'Anticipez vos paiements à l’URSSAF, simulez votre impôt sur le revenu et faites-vous une idée de ce que vous gagnez réellement. Vous pourrez modifier vos informations à tout moment depuis le bouton “Actualiser ma situation”.',
          title: 'Estimez vos revenus en temps réel',
        },
        netIncome: 'Revenu restant',
        revenue: 'Chiffre d’affaires de la période',
        settings: 'Actualiser ma situation',
        socialContributions: 'Cotisations',
        tax: 'Impôts',
        tooltip: {
          complementaryRevenue: 'Complément de revenu : {amount} TTC',
          revenue: 'Chiffre d’affaires Shine : {amount} TTC',
          title:
            'Montant estimé sur la base de vos paiements entrants (à l’exception des paiements marqués comme “personnels” dans l’application Shine).',
          vatAmount: 'TVA encaissée : {amount}',
        },
        vatWithAmount: 'TVA encaissée : {amount}',
        withoutTax: 'HT',
      },
      readTheManual: "Parcourir le mode d'emploi",
      settings: {
        cta: {
          subtitle:
            'Gardez vos informations à jour pour anticiper vos paiements à l’URSSAF le plus précisément possible.',
          text: 'Actualiser ma situation',
          title: 'Ma situation a changé',
        },
        frequency: {
          activeFrom: 'Actif à partir du {date}',
          description: 'Périodicité de déclaration des revenus',
          monthly: 'Tous les mois',
          quarterly: 'Tous les trimestres',
        },
        options: {
          accre: 'Je bénéficie de l’exonération de début d’activité ou Acre',
          description: 'Mes options',
          libDescription: 'Versement libératoire de l’impôt sur le revenu',
          liberatoire: 'J’ai choisi le versement libératoire',
          vat: 'Je suis redevable de la TVA',
        },
        title: 'Paramétrez votre situation',
      },
      subtitle:
        'Estimez les cotisations de votre micro-entreprise et visualisez vos échéances de paiement.',
      tabs: {
        history: 'Historique',
        simulator: 'Simulateur',
      },
      taxDetails: {
        accordionTitle: 'Détail de vos cotisations',
        amountPaid: 'Montant du règlement URSSAF',
        ctaNotPaid: 'Marquer comme non payée',
        ctaPaid: 'Marquer comme payée',
        rate: {
          [TaxTypeEnum.Cfp]: '+ Contribution à la formation professionnelle',
          [TaxTypeEnum.FraisChambre]: '+ Taxes pour frais de chambre',
          [TaxTypeEnum.PrelevLib]: 'Impôt sur le revenu',
          [TaxTypeEnum.Rsi]: 'Cotisations sociales',
        },
        status: {
          late: 'Retard de paiement',
          paid: 'Payée',
          pending: 'En cours',
        },
        vat: 'TVA encaissée',
      },
      title: 'Cotisations sociales',
    },
  } as const,
  {
    customLanguageInterface: getMobileAppLang,
  },
);

export default locales;
